import { ParsedUrlQuery } from 'node:querystring'
import Container from 'components/Container'
import CourseDetails from 'components/CourseDetails/CourseDetails'
import Layout from 'components/layouts/Layout'
import { GET_PUBLIC_COURSE } from 'gql/courses'
import usePageHeader from 'hooks/usePageHeader'
import { initializeApollo } from 'lib/apolloClient'
import getSettings from 'lib/settings'
import { GetServerSideProps } from 'next'
import { getSession } from 'next-auth/client'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import React from 'react'
import { Course } from 'types'

interface PublicCoursePageProps {
  course: Course
}

interface PublicCoursePageParams extends ParsedUrlQuery {
  slug: string
}

const PublicCoursePage = ({ course }: PublicCoursePageProps) => {
  const { t } = useTranslation()

  usePageHeader({
    title: course?.title,
    titlePrefix: undefined,
    backLinkHref: '/',
    backLinkText: t('Home'),
    loading: false,
  })

  return (
    <>
      <Head>
        <title>{course.title}</title>
      </Head>

      <Container>
        <CourseDetails course={course} />
      </Container>
    </>
  )
}

PublicCoursePage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>
}

PublicCoursePage.auth = false

export const getServerSideProps: GetServerSideProps<
  PublicCoursePageProps,
  PublicCoursePageParams
> = async (ctx) => {
  const { params } = ctx
  const { slug } = params || {}
  if (!slug) {
    return {
      notFound: true,
    }
  }

  const apolloClient = initializeApollo()
  try {
    const pageResult = await apolloClient.query<{
      course: Course
    }>({
      query: GET_PUBLIC_COURSE,
      variables: {
        slug,
      },
    })

    if (pageResult.data.course) {
      return {
        props: {
          session: await getSession(ctx),
          course: pageResult.data.course,
          ...(await serverSideTranslations(ctx.locale || '')),
          ...(await getSettings()),
        },
      }
    }
  } catch (error) {
    console.error(error, 'error')
  }

  // Not found
  return {
    notFound: true,
  }
}

export default PublicCoursePage
